import { useState, useEffect } from 'react';
import { PanelRightClose, MapPinned, ExternalLink } from 'lucide-react';
import axios from 'axios';

export default function MapPanel({ jobsInView = [], onClose }) {
    const [locationData, setLocationData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const jobWithLocation = jobsInView.find(j => j.location && j.location.trim() !== '' && j.location !== 'No location');

    useEffect(() => {
        if (!jobWithLocation) {
            setLocationData(null);
            return;
        }

        const fetchLocationInfo = async () => {
            setLoading(true);
            setError(null);

            try {
                const token = localStorage.getItem("company_token");

                const res = await axios.get(
                    `https://api.admin.boat.saasbizhub.com/api/v1/company/jobs/${jobWithLocation.id}/location`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (res.data.success) {
                    setLocationData(res.data.data);
                }
            } catch (err) {
                setError("Failed to load map data");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchLocationInfo();
    }, [jobWithLocation?.id]);


    return (
        <div className="w-96 bg-white border-l border-gray-200 flex flex-col text-sm">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                    <MapPinned className="w-5 h-5 text-emerald-600" />
                    Map View
                </h3>
                <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
                    <PanelRightClose className="w-5 h-5" />
                </button>
            </div>

            <div className="flex-1 flex flex-col">
                {loading && (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                )}

                {error && !loading && (
                    <div className="flex-1 flex items-center justify-center text-red-600">
                        {error}
                    </div>
                )}

                {!jobWithLocation && !loading && (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                        <p>No job with location in current view</p>
                    </div>
                )}

                {locationData && !loading && (
                    <>
                        <div className="p-4 border-b border-gray-200">
                            <p className="font-bold text-lg text-gray-900">
                                {locationData.location_info.boat_location}
                            </p>
                            <p className="text-gray-600 mt-1">
                                {locationData.location_info.full_address}
                            </p>

                            <div className="flex gap-3 mt-4">
                                <a
                                    href={locationData.google_maps_links.search_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-center font-medium hover:bg-gray-200 flex items-center justify-center gap-2"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Open in Maps
                                </a>
                                <a
                                    href={locationData.google_maps_links.directions_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 px-3 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg text-center font-bold"
                                >
                                    Get Directions
                                </a>
                            </div>
                        </div>

                        <div className="flex-1">
                            <iframe
                                title="Location Map"
                                src={locationData.embed_url}
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            ></iframe>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}